/**
 * Delivery Challan Screen (6.4.1)
 * POS Module - Delivery
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Truck, Edit2, Trash2, Plus, Save, RefreshCw, Download, Printer, X, Search } from 'lucide-react';
import { deliveryChallan, customerMaster } from '@/services/api/posService';

const SCREEN_ID = '6.4.1';

const DeliveryChallanScreen = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form state
  const [form, setForm] = useState({
    DCNo: '',
    DCDate: new Date().toISOString().split('T')[0],
    Customer: '',
    CustomerId: '',
    SalesOrder: '',
    Items: [],
    TotalQty: 0,
    TotalValue: 0,
    Vehicle: '',
    Driver: '',
    Remarks: '',
    Status: 'Pending'
  });

  // Mock customers
  const [customers] = useState([
    { id: 'CUS001', name: 'ABC Enterprises' },
    { id: 'CUS002', name: 'XYZ Retail' },
  ]);

  // Load data
  const handleLoadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await deliveryChallan.getAll();
      setData(res.data?.data || []);
    } catch (e) {
      // Mock data
      setData([
        { DCNo: 'DC/2024/001', DCDate: '2024-03-14', Customer: 'ABC Enterprises', SalesOrder: 'SO/2024/001', TotalQty: 10, TotalValue: 15000, Status: 'Pending' },
        { DCNo: 'DC/2024/002', DCDate: '2024-03-13', Customer: 'XYZ Retail', SalesOrder: 'SO/2024/002', TotalQty: 25, TotalValue: 28000, Status: 'Delivered' },
        { DCNo: 'DC/2024/003', DCDate: '2024-03-12', Customer: 'PQR Trading', SalesOrder: '-', TotalQty: 5, TotalValue: 8500, Status: 'Cancelled' },
      ]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    handleLoadData();
  }, [handleLoadData]);

  // Save
  const handleSave = async () => {
    if (!form.Customer) {
      alert('Customer is required');
      return;
    }
    
    if (editItem) {
      setData(prev => prev.map(d => d.DCNo === editItem.DCNo ? {...d, ...form} : d));
    } else {
      const newDCNo = `DC/2024/${String(data.length + 1).padStart(3, '0')}`;
      setData(prev => [...prev, { ...form, DCNo: newDCNo }]);
    }
    clearForm();
    setShowForm(false);
  };

  // Delete
  const handleDelete = async () => {
    if (!window.confirm(`Delete ${selectedItems.length} challan(s)?`)) return;
    setData(prev => prev.filter(d => !selectedItems.includes(d.DCNo)));
    setSelectedItems([]);
  };

  const clearForm = () => {
    setForm({
      DCNo: '', DCDate: new Date().toISOString().split('T')[0], Customer: '', CustomerId: '',
      SalesOrder: '', Items: [], TotalQty: 0, TotalValue: 0, Vehicle: '', Driver: '',
      Remarks: '', Status: 'Pending'
    });
    setEditItem(null);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({ ...form, ...item });
    setShowForm(true);
  };

  const toggleSelect = (id) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const filteredData = data.filter(d => 
    d.Customer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.DCNo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-500/10 text-yellow-500';
      case 'Delivered': return 'bg-green-500/10 text-green-500';
      case 'Cancelled': return 'bg-red-500/10 text-red-500';
      default: return 'bg-slate-500/10 text-slate-500';
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);

  const inputCls = `w-full px-3 py-2 rounded border text-sm ${isDark ? 'bg-[#111] border-[#333] text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const labelCls = `block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`;
  const btnCls = `px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 ${isDark ? 'bg-[#1a1a1e] text-slate-300 hover:bg-[#252528]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`;

  return (
    <div className={`h-full flex flex-col ${isDark ? 'bg-[#0a0a0c]' : 'bg-gray-50'}`} data-testid="delivery-challan-screen">
      {/* Header */}
      <div className="px-4 py-2 bg-orange-600 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Truck size={18} />
          <span className="font-semibold text-sm">Delivery Challan</span>
          <span className="text-[10px] px-2 py-0.5 rounded bg-white/20">{SCREEN_ID}</span>
        </div>
        <span className="text-[10px] opacity-75">TRANSACTION</span>
      </div>

      {/* Toolbar */}
      <div className={`px-3 py-2 border-b flex items-center justify-between ${isDark ? 'border-[#333] bg-[#0e0e10]' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-center gap-2">
          <button onClick={() => { clearForm(); setShowForm(true); }} className="px-3 py-1.5 rounded bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium flex items-center gap-1.5">
            <Plus size={12} /> New Challan
          </button>
          {selectedItems.length > 0 && (
            <button onClick={handleDelete} className="px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-medium flex items-center gap-1.5">
              <Trash2 size={12} /> Delete ({selectedItems.length})
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..." className={`pl-7 pr-3 py-1.5 rounded border text-xs w-48 ${isDark ? 'bg-[#111] border-[#333] text-white' : 'bg-white border-gray-300'}`} />
          </div>
          <button onClick={handleLoadData} disabled={loading} className={btnCls}>
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button className={btnCls}><Download size={12} /> Export</button>
          <button className={btnCls}><Printer size={12} /> Print</button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Data Grid */}
        <div className={`flex-1 overflow-auto ${showForm ? 'border-r' : ''} ${isDark ? 'border-[#333]' : 'border-gray-200'}`}>
          <table className="w-full text-xs">
            <thead className={`sticky top-0 z-10 ${isDark ? 'bg-[#0e0e10]' : 'bg-gray-50'}`}>
              <tr className={`border-b ${isDark ? 'border-[#222]' : 'border-gray-200'}`}>
                <th className="px-2 py-2 w-8">
                  <input type="checkbox" onChange={(e) => setSelectedItems(e.target.checked ? data.map(d => d.DCNo) : [])}
                    checked={selectedItems.length === data.length && data.length > 0} className="accent-orange-500" />
                </th>
                <th className={`px-3 py-2 text-left text-[10px] font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>DC No</th>
                <th className={`px-3 py-2 text-left text-[10px] font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Date</th>
                <th className={`px-3 py-2 text-left text-[10px] font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Customer</th>
                <th className={`px-3 py-2 text-left text-[10px] font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Sales Order</th>
                <th className={`px-3 py-2 text-right text-[10px] font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Total Qty</th>
                <th className={`px-3 py-2 text-right text-[10px] font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Total Value</th>
                <th className={`px-3 py-2 text-left text-[10px] font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Status</th>
                <th className={`px-3 py-2 text-center text-[10px] font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">Loading...</td></tr>
              ) : filteredData.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">No challans found.</td></tr>
              ) : filteredData.map((r) => (
                <tr key={r.DCNo} className={`border-b cursor-pointer transition-colors ${selectedItems.includes(r.DCNo) ? isDark ? 'bg-orange-900/20' : 'bg-orange-50' : isDark ? 'border-[#1a1a1e] hover:bg-[#111]' : 'border-gray-100 hover:bg-gray-50'}`} onClick={() => openEdit(r)}>
                  <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedItems.includes(r.DCNo)} onChange={() => toggleSelect(r.DCNo)} className="accent-orange-500" />
                  </td>
                  <td className={`px-3 py-1.5 font-mono font-medium ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>{r.DCNo}</td>
                  <td className={`px-3 py-1.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{r.DCDate}</td>
                  <td className={`px-3 py-1.5 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{r.Customer}</td>
                  <td className={`px-3 py-1.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{r.SalesOrder || '-'}</td>
                  <td className={`px-3 py-1.5 text-right ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{r.TotalQty}</td>
                  <td className={`px-3 py-1.5 text-right font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(r.TotalValue)}</td>
                  <td className="px-3 py-1.5"><span className={`px-2 py-0.5 rounded text-[10px] font-medium ${getStatusColor(r.Status)}`}>{r.Status}</span></td>
                  <td className="px-3 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => openEdit(r)} className={`p-1 rounded ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-gray-100 text-gray-500'}`}><Edit2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Form Panel */}
        {showForm && (
          <div className={`w-96 flex flex-col ${isDark ? 'bg-[#0a0a0c]' : 'bg-gray-50'}`}>
            <div className="flex items-center justify-between p-4 pb-2">
              <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{editItem ? 'Edit Challan' : 'New Delivery Challan'}</h3>
              <button onClick={() => { setShowForm(false); clearForm(); }} className={`p-1 rounded ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-gray-200 text-gray-500'}`}><X size={16} /></button>
            </div>

            <div className="flex-1 overflow-auto p-4 pt-2 space-y-3">
              <div><label className={labelCls}>DC No</label><input value={form.DCNo} readOnly placeholder="Auto-generated" className={`${inputCls} ${isDark ? 'bg-[#1a1a1e]' : 'bg-gray-100'}`} /></div>
              <div><label className={labelCls}>DC Date</label><input type="date" value={form.DCDate} onChange={(e) => setForm(p => ({...p, DCDate: e.target.value}))} className={inputCls} /></div>
              <div><label className={labelCls}>Customer <span className="text-red-500">*</span></label>
                <select value={form.CustomerId} onChange={(e) => {
                  const customer = customers.find(c => c.id === e.target.value);
                  setForm(p => ({...p, CustomerId: e.target.value, Customer: customer?.name || ''}));
                }} className={inputCls}>
                  <option value="">-- Select Customer --</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>Against Sales Order</label><input value={form.SalesOrder} onChange={(e) => setForm(p => ({...p, SalesOrder: e.target.value}))} placeholder="Optional" className={inputCls} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Total Qty</label><input type="number" value={form.TotalQty} onChange={(e) => setForm(p => ({...p, TotalQty: parseInt(e.target.value) || 0}))} className={inputCls} /></div>
                <div><label className={labelCls}>Total Value</label><input type="number" value={form.TotalValue} onChange={(e) => setForm(p => ({...p, TotalValue: parseFloat(e.target.value) || 0}))} className={inputCls} /></div>
              </div>
              <div><label className={labelCls}>Vehicle No</label><input value={form.Vehicle} onChange={(e) => setForm(p => ({...p, Vehicle: e.target.value}))} className={inputCls} /></div>
              <div><label className={labelCls}>Driver Name</label><input value={form.Driver} onChange={(e) => setForm(p => ({...p, Driver: e.target.value}))} className={inputCls} /></div>
              <div><label className={labelCls}>Remarks</label><textarea value={form.Remarks} onChange={(e) => setForm(p => ({...p, Remarks: e.target.value}))} rows={2} className={inputCls} /></div>
              <div><label className={labelCls}>Status</label>
                <select value={form.Status} onChange={(e) => setForm(p => ({...p, Status: e.target.value}))} className={inputCls}>
                  <option value="Pending">Pending</option>
                  <option value="Delivered">Delivered</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            <div className={`flex gap-2 p-4 border-t ${isDark ? 'border-[#333]' : 'border-gray-200'}`}>
              <button onClick={handleSave} className="flex-1 px-3 py-2 rounded bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium flex items-center justify-center gap-1">
                <Save size={12} /> {editItem ? 'Update' : 'Save'}
              </button>
              <button onClick={clearForm} className={`px-3 py-2 rounded text-xs font-medium ${isDark ? 'bg-[#1a1a1e] text-slate-300' : 'bg-gray-200 text-gray-700'}`}>Clear</button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={`px-4 py-1.5 border-t text-[10px] flex items-center justify-between ${isDark ? 'border-[#333] bg-[#0e0e10] text-slate-500' : 'border-gray-200 bg-gray-50 text-gray-500'}`}>
        <span>{filteredData.length} challans</span>
        <span>Screen: {SCREEN_ID}</span>
      </div>
    </div>
  );
};

export default DeliveryChallanScreen;
